/**
 * Accessibility Utilities
 * Helper functions for screen reader announcements and keyboard navigation
 */

/**
 * Announce a message to screen readers
 * Creates a temporary live region to announce messages
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  // Create or get the live region
  let liveRegion = document.getElementById('sr-announcer');
  
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'sr-announcer';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    document.body.appendChild(liveRegion);
  } else {
    // Update priority if needed
    liveRegion.setAttribute('aria-live', priority);
  }
  
  // Clear previous message
  liveRegion.textContent = '';
  
  // Announce new message after a brief delay to ensure screen readers pick it up
  setTimeout(() => {
    const region = document.getElementById('sr-announcer');
    if (region) {
      region.textContent = message;
    }
  }, 100);
  
  // Clear message after announcement
  setTimeout(() => {
    const region = document.getElementById('sr-announcer');
    if (region) {
      region.textContent = '';
    }
  }, 5000);
}

/**
 * Focus the first focusable element within a container
 */
export function focusFirstElement(container: HTMLElement): void {
  const focusableElements = container.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  if (focusableElements.length > 0) {
    const firstElement = focusableElements[0];
    if (firstElement) {
      firstElement.focus();
    }
  }
}

/**
 * Trap focus within a container (for modals/dialogs)
 */
export function trapFocus(container: HTMLElement): () => void {
  const focusableElements = container.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  
  const handleTabKey = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  };
  
  container.addEventListener('keydown', handleTabKey);
  
  // Return cleanup function
  return () => {
    container.removeEventListener('keydown', handleTabKey);
  };
}

/**
 * Get a descriptive label for a composition action
 */
export function getCompositionActionLabel(
  action: 'load' | 'rename' | 'duplicate' | 'delete',
  compositionTitle: string
): string {
  const labels = {
    load: `Load composition "${compositionTitle}" into the sequencer`,
    rename: `Rename composition "${compositionTitle}"`,
    duplicate: `Create a copy of composition "${compositionTitle}"`,
    delete: `Delete composition "${compositionTitle}" permanently`,
  };
  
  return labels[action];
}

/**
 * Format a date for screen readers
 */
export function formatDateForScreenReader(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get keyboard navigation instructions for a component
 */
export function getKeyboardInstructions(component: 'grid' | 'list' | 'dialog'): string {
  const instructions = {
    grid: 'Use arrow keys to navigate between compositions. Press Enter or Space to load a composition.',
    list: 'Use Tab to navigate between actions. Press Enter or Space to activate.',
    dialog: 'Press Escape to close. Use Tab to navigate between fields.',
  };
  
  return instructions[component];
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if user prefers high contrast
 */
export function prefersHighContrast(): boolean {
  return window.matchMedia('(prefers-contrast: high)').matches;
}

/**
 * Create a unique ID for accessibility purposes
 */
let idCounter = 0;
export function generateA11yId(prefix: string): string {
  idCounter++;
  return `${prefix}-${idCounter}-${Date.now()}`;
}
